import { useEffect, useState } from 'react';
import {
  X,
  Save,
  AlertCircle,
  XCircle,
  FileText,
  Clock,
  Calendar,
  UserCheck,
  Send,
} from 'lucide-react';
import { useGetApplicationByIdQuery, useUpdateApplicationWorkInfoMutation } from '../store/services/applicationApi';
import { useGetSettingsQuery } from '../store/services/settingsApi';
import WorkInfoModal from './WorkInfoModal';

// Application Status Enum
const ApplicationStatus = {
  APPLIED: 'applied',
  SCHEDULED: 'scheduled',
  SELECTED: 'selected',
  UNDER_REVIEW: 'under-review',
  SUBMITTED: 'submitted',
  REJECTED: 'rejected',
};

const StatusModal = ({ isOpen, onClose, application, onUpdateStatus }: any) => {
  const [status, setStatus] = useState(application?.status);
  const [rejectionReason, setRejectionReason] = useState(application?.rejectionReason || '');
  const [remarkText, setRemarkText] = useState(application?.remarkText || '');
  const [adminNotes, setAdminNotes] = useState(application?.adminNotes || '');
  const [isLoading, setIsLoading] = useState(false);
  const [workInfoModalOpen, setWorkInfoModalOpen] = useState(false);
  const { data: applicationDetailsResponse, refetch: refetchApplicationDetails } = useGetApplicationByIdQuery(application?._id, {
    skip: !isOpen || !application?._id,
  });
  const { data: settingsResponse } = useGetSettingsQuery();
  const [updateApplicationWorkInfo] = useUpdateApplicationWorkInfoMutation();

  const profile = applicationDetailsResponse?.data?.profile ?? application?.profile ?? {};
  const workInfo = profile?.workInfo || {};
  const settings = settingsResponse?.data;

  const statusOptions = [
    {
      value: ApplicationStatus.APPLIED,
      label: 'Applied',
      color: 'bg-blue-100 text-blue-800 border-blue-300',
      icon: Clock,
    },
    {
      value: ApplicationStatus.SCHEDULED,
      label: 'Scheduled',
      color: 'bg-purple-100 text-purple-800 border-purple-300',
      icon: Calendar,
    },    
    {
      value: ApplicationStatus.SELECTED,
      label: 'Selected',
      color: 'bg-green-100 text-green-800 border-green-300',
      icon: UserCheck,
    },
    {
      value: ApplicationStatus.UNDER_REVIEW,
      label: 'Under Review',
      color: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      icon: AlertCircle,
    },
    {
      value: ApplicationStatus.SUBMITTED,
      label: 'Submitted',
      color: 'bg-indigo-100 text-indigo-800 border-indigo-300',
      icon: Send,
    },
    {
      value: ApplicationStatus.REJECTED,
      label: 'Rejected',
      color: 'bg-red-100 text-red-800 border-red-300',
      icon: XCircle,
    },
  ];

  useEffect(() => {
    if (application) {
      setStatus(application.status || '');
      setRejectionReason(application.rejectionReason || '');
      setRemarkText(application.remarkText || '');
      setAdminNotes(application.adminNotes || '');
    }
  }, [application]);

  const hasRequiredWorkInfo = (value: any) =>
    Boolean(value?.employeeId && value?.projectName && value?.branch && value?.shift);

  const buildScheduledRemarkText = () => {
    const jobTitle = settings?.jobTitle || '[job title]';
    const hrManagerName = settings?.hrManagerName || '[HR Manager]';
    const interviewDate = settings?.interviewDate || 'To be announced';
    const interviewTime = settings?.interviewTime || 'To be announced';

    return `Dear Candidate,
We are pleased to inform you that you have been shortlisted for an interview for the position of ${jobTitle} at Intelligent Image Management Ltd.
Interview Details:
Date: ${interviewDate}
Time: ${interviewTime}
Venue: Intelligent Image Management Ltd.

KA-53/2, Pragati Sarani, Shahjadpur
(Opposite Suvastu Nazar Valley Shopping Complex), Dhaka, Bangladesh

Please arrive on time and bring any necessary documents.
We look forward to meeting you.
Best regards,

${hrManagerName}
Intelligent Image Management Ltd.`;
  };

  const buildSelectedRemarkText = (currentWorkInfo: any) => {
    const candidateName = profile?.basic?.fullName || application?.name || 'Candidate';
    const jobTitle = settings?.jobTitle || '[Job Title]';
    const projectName = settings?.projectName || currentWorkInfo?.projectName || '(Name)';
    const hrManagerName = settings?.hrManagerName || '[HR Manager]';
    const employeeId = currentWorkInfo?.employeeId || '(EMID)';

    return `Dear ${candidateName},

Congratulations! We are pleased to inform you that you have been selected for the position of ${jobTitle} Project: ${projectName} at our organization.

Our HR team will contact you shortly with further details regarding your joining process. Your Employment ID will be: ${employeeId}.

Welcome to the team!

Best regards,

${hrManagerName}
Intelligent Image Management Ltd.`;
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const updateData = {
        status,
        adminNotes,
        ...(status === ApplicationStatus.REJECTED && rejectionReason && { rejectionReason }),
        ...((status === ApplicationStatus.SCHEDULED || status === ApplicationStatus.SELECTED) && remarkText && { remarkText }),
      };

      await onUpdateStatus(application._id, updateData);
      onClose();
    } catch (error) {
      console.error('Error updating status:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusChange = (newStatus: any) => {
    if (newStatus === ApplicationStatus.SCHEDULED) {
      const hasSavedRemark = application?.status === ApplicationStatus.SCHEDULED && application?.remarkText?.trim();
      setRemarkText(hasSavedRemark ? application.remarkText : buildScheduledRemarkText());
    }

    if (newStatus === ApplicationStatus.SELECTED) {
      if (!hasRequiredWorkInfo(workInfo)) {
        setWorkInfoModalOpen(true);
        return;
      }

      const hasSavedRemark = application?.status === ApplicationStatus.SELECTED && application?.remarkText?.trim();
      setRemarkText(hasSavedRemark ? application.remarkText : buildSelectedRemarkText(workInfo));
    }

    if (newStatus !== ApplicationStatus.SCHEDULED && newStatus !== ApplicationStatus.SELECTED) {
      setRemarkText('');
    }

    setStatus(newStatus);
    if (newStatus !== ApplicationStatus.REJECTED) {
      setRejectionReason('');
    }
  };

  const handleSaveWorkInfo = async (payload: {
    employeeId: string;
    projectName: string;
    branch: string;
    shift: string;
    reference?: string;
  }) => {
    await updateApplicationWorkInfo({
      id: application._id,
      ...payload,
    }).unwrap();
    await refetchApplicationDetails();
    setStatus(ApplicationStatus.SELECTED);
    setRemarkText(buildSelectedRemarkText(payload));
  };

  if (!isOpen) return null;

  return (
    <>
      {!workInfoModalOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-3 sm:items-center sm:p-4">
          <div className="my-4 flex max-h-[calc(100vh-2rem)] w-full max-w-2xl flex-col overflow-hidden rounded-lg bg-white shadow-2xl sm:my-0">
            {/* Header */}
            <div className="shrink-0 border-b border-gray-200 bg-white px-4 py-3 sm:px-6">
              <div className="flex items-center justify-between">
                <div className="flex min-w-0 items-center space-x-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-r from-blue-500 to-indigo-600">
                    <FileText className="w-4 h-4 text-white" />
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-base font-semibold text-gray-900">Update Application Status</h2>
                    <p className="truncate text-sm text-gray-500">
                      Candidate ID: {application?.applicationId || application?._id}
                    </p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="rounded-md border border-gray-200 bg-white p-2 text-gray-500 transition hover:bg-gray-100"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="overflow-y-auto">
              <div className="space-y-5 p-4 sm:space-y-6 sm:p-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Application Status *
                  </label>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                    {statusOptions.map((option) => {
                      const IconComponent = option.icon;
                      return (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => handleStatusChange(option.value)}
                          className={`flex min-h-[56px] items-center rounded-lg border transition-all duration-200 ${
                            status === option.value
                              ? `${option.color} border-current shadow-sm`
                              : 'bg-white border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="flex items-center space-x-2 px-3 py-3 sm:space-x-3 sm:px-4">
                            <IconComponent
                              className={`w-5 h-5 ${
                                status === option.value ? 'text-current' : 'text-gray-400'
                              }`}
                            />
                            <span
                              className={`text-sm font-medium sm:text-base ${
                                status === option.value ? 'text-current' : 'text-gray-700'
                              }`}
                            >
                              {option.label}
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
                {status === ApplicationStatus.REJECTED && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Rejection Reason *
                    </label>
                    <textarea
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      rows={3}
                      className="w-full rounded-md border border-gray-300 px-4 py-3 focus:border-red-500 focus:ring-2 focus:ring-red-500 resize-none"
                      placeholder="Please provide a reason for rejection..."
                      required
                    />
                  </div>
                )}
                {(status === ApplicationStatus.SCHEDULED || status === ApplicationStatus.SELECTED) && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Remark Text
                    </label>
                    <textarea
                      value={remarkText}
                      onChange={(e) => setRemarkText(e.target.value)}
                      rows={3}
                      className="w-full rounded-md border border-gray-300 px-4 py-3 focus:border-red-500 focus:ring-2 focus:ring-red-500 resize-none"
                      placeholder="Enter remark text..."
                    />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notes
                  </label>
                  <textarea
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    rows={4}
                    className="w-full rounded-md border border-gray-300 px-4 py-3 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 resize-none"
                    placeholder="Add any internal notes about this application..."
                  />
                </div>
                
                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={onClose}
                    className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={
                      isLoading ||
                      (status === ApplicationStatus.REJECTED && !rejectionReason.trim())
                    }
                    className="inline-flex items-center gap-2 rounded-md bg-[#c31200] px-4 py-2 text-sm font-medium text-white transition hover:bg-[#a71000] disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {isLoading ? (
                      <>
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                        <span>Updating...</span>
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        <span>Update Status</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <WorkInfoModal
        isOpen={workInfoModalOpen}
        onClose={() => {
          setWorkInfoModalOpen(false);
          if (!hasRequiredWorkInfo(workInfo)) {
            setStatus(application?.status || '');
          }
        }}
        initialWorkInfo={workInfo}
        onSave={handleSaveWorkInfo}
      />
    </>
  );
};

export default StatusModal;
